import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  extractWaTemplateParameters,
  resolveWaTemplateParameterFormat,
  WA_COPY_CODE_FIXED_LABEL,
  WA_TEMPLATE_NAMED_PARAMETER_PATTERN,
  WA_ORDER_DETAILS_CATEGORIES,
  WA_ORDER_DETAILS_FIXED_LABEL,
  WA_ORDER_DETAILS_HEADER_FORMATS,
  WA_TEMPLATE_LIMITS,
  WaTemplateButtonDefinition,
  WaTemplateButtonType,
  WaTemplateComponentRead,
  WaTemplateDefinition,
  WaTemplateEditDefinition,
  WaTemplateNamedExample,
  WaTemplateParameterFormat,
  WaTemplateRead,
} from 'lib-trend-core';

export type TemplateHeaderFormatOption = 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'LOCATION';

/**
 * A rule Meta enforces that a field validator cannot express. `field` is the form path the message
 * belongs next to (RF-28); a problem with no `field` has no single field to blame.
 */
export interface TemplateRuleProblem {
  key: string;
  params?: { [key: string]: unknown };
  field?: string;
}

/** The five button families of RF-49, keyed by the provider type each UI type maps onto. */
export const BUTTON_FAMILY: { [type: string]: 'CALL_TO_ACTION' | 'QUICK_REPLY' } = {
  URL: 'CALL_TO_ACTION',
  VOICE_CALL: 'CALL_TO_ACTION',
  PHONE_NUMBER: 'CALL_TO_ACTION',
  COPY_CODE: 'CALL_TO_ACTION',
  ORDER_DETAILS: 'CALL_TO_ACTION',
  QUICK_REPLY: 'QUICK_REPLY',
};

/** Label fixed by the provider (RF-44) — shown as it comes, never translated. */
export const COPY_CODE_FIXED_LABEL = WA_COPY_CODE_FIXED_LABEL;

/**
 * Category of a template rendered as order details. Both UTILITY and MARKETING are accepted by the
 * provider; UTILITY is chosen because a Pix charge is a transactional message — it bills as utility
 * and is not subject to the per-user marketing caps.
 */
export const ORDER_DETAILS_CATEGORY: string = 'UTILITY';

/**
 * Label fixed by Meta, exactly like COPY_CODE's. Not a default and not translatable: a template
 * carrying anything else is refused with «O texto do tipo de botão "ORDER_DETAILS" não pode ser
 * modificado e sempre deve ser "Copy Pix code"». It stays in English because that is the literal
 * string the provider compares against.
 */
export const ORDER_DETAILS_FIXED_LABEL = WA_ORDER_DETAILS_FIXED_LABEL;

/**
 * Everything the template form knows how to do to itself: keeping the sample inputs in step with the
 * variables in the text, inserting variables, managing the button rows, turning the form into a
 * provider definition, and reading a provider template back into it.
 *
 * It is a plain class, not a component or a service: the state is per-form, and keeping it out of the
 * components lets step 2 be split into as many child components as the screen needs while the form
 * and its derived state stay in exactly one place.
 *
 * SPEC-2026-08-whatsapp-cloud-api-template-builder — T-10 … T-16, T-18, T-19.
 */
export class TemplateFormEngine {

  readonly limits = WA_TEMPLATE_LIMITS;

  /** Parameters currently used by each component, in order of appearance, without repetition. */
  headerParameters: Array<string> = [];
  bodyParameters: Array<string> = [];

  /** Parameter format the template had when it was loaded — only a real change is sent on edit. */
  initialParameterFormat: WaTemplateParameterFormat = null;

  /** Media already attached to a loaded template: a URL, not a reusable handle. */
  currentMediaUrl: string = null;
  mediaFileName: string = null;

  /**
   * Sample values typed so far, keyed by `{scope}:{parameter}`. Survives a variable being deleted
   * and typed again in the same session (RF-24).
   */
  private readonly exampleValues = new Map<string, string>();
  private previousHeaderParameters: Array<string> = [];
  private previousBodyParameters: Array<string> = [];

  constructor(
    private readonly formGroup: FormGroup,
    private readonly formBuilder: FormBuilder,
    /**
     * Payments API - Brazil mode (DEV-1992). The template is created with
     * `displayFormat: ORDER_DETAILS` and exactly one ORDER_DETAILS button, which is what makes the
     * Pix code deliverable as a button action parameter at send time. Everything the format forbids
     * — other button types, a VIDEO or LOCATION header — is closed off rather than validated late.
     */
    readonly pixMode: boolean = false,
  ) { }

  /**
   * The single ORDER_DETAILS button of a Pix template. The format requires exactly one and accepts
   * no other, so it is seeded instead of being offered in the add menu.
   */
  seedOrderDetailsButton(): void {
    this.buttons.clear();
    this.buttons.push(this.buildButtonGroup('ORDER_DETAILS'));
  }

  // ---------------------------------------------------------------- accessors

  get headerExamples(): FormArray {
    return this.formGroup.get('headerExamples') as FormArray;
  }

  get bodyExamples(): FormArray {
    return this.formGroup.get('bodyExamples') as FormArray;
  }

  get buttons(): FormArray {
    return this.formGroup.get('buttons') as FormArray;
  }

  get parameterFormat(): WaTemplateParameterFormat {
    return this.formGroup.get('parameterFormat').value;
  }

  get headerFormat(): TemplateHeaderFormatOption {
    return this.formGroup.get('headerFormat').value;
  }

  isMediaHeader(format: TemplateHeaderFormatOption = this.headerFormat): boolean {
    return format === 'IMAGE' || format === 'VIDEO' || format === 'DOCUMENT';
  }

  /** RF-17 — the header text exists only while no media sample is chosen. */
  get headerTextEnabled(): boolean {
    return this.headerFormat === 'NONE' || this.headerFormat === 'TEXT';
  }

  // --------------------------------------------------------------- parameters

  /**
   * Rebuilds the sample inputs whenever the variables used in the header or in the body change,
   * preserving what was already typed for a variable that is still there (RF-23, RF-24).
   */
  syncParameters(): void {
    const headerParameters = this.headerTextEnabled
      ? extractWaTemplateParameters(this.formGroup.get('headerText').value)
      : [];
    const bodyParameters = extractWaTemplateParameters(this.formGroup.get('body').value);

    if (headerParameters.join('|') !== this.previousHeaderParameters.join('|')) {
      this.cacheExamples(this.headerExamples, this.previousHeaderParameters, 'header');
      this.rebuildExamples(this.headerExamples, headerParameters, 'header');
      this.previousHeaderParameters = headerParameters;
    }

    if (bodyParameters.join('|') !== this.previousBodyParameters.join('|')) {
      this.cacheExamples(this.bodyExamples, this.previousBodyParameters, 'body');
      this.rebuildExamples(this.bodyExamples, bodyParameters, 'body');
      this.previousBodyParameters = bodyParameters;
    }

    this.headerParameters = headerParameters;
    this.bodyParameters = bodyParameters;
  }

  private cacheExamples(array: FormArray, parameters: Array<string>, scope: string): void {
    array.controls.forEach((control, index) => {
      const parameter = parameters[index];
      if (!!parameter) {
        this.exampleValues.set(`${scope}:${parameter}`, control.value ?? '');
      }
    });
  }

  /**
   * Resizes the sample inputs to the current parameters and reassigns their values.
   *
   * The array is RESIZED, never cleared and refilled. `FormControlName` resolves its control once —
   * when its own input changes — and `[formControlName]="i"` keeps the same index across a rebuild.
   * Replacing the instance behind a live index therefore leaves the directive bound to a control
   * that is no longer in the array: the operator's text lands on a detached control and stays on
   * screen, while the control the form validates is still empty and required. That is a field that
   * looks filled and reports itself as missing, with nothing on screen explaining the contradiction.
   *
   * Keeping the instances also keeps the rendered views alive, so nothing depends on how the loop
   * happens to be tracked.
   */
  private rebuildExamples(array: FormArray, parameters: Array<string>, scope: string): void {
    while (array.length > parameters.length) {
      array.removeAt(array.length - 1);
    }
    while (array.length < parameters.length) {
      array.push(this.formBuilder.control('', [Validators.required]));
    }

    parameters.forEach((parameter, index) => {
      array.at(index).setValue(this.exampleValues.get(`${scope}:${parameter}`) ?? '');
    });
  }

  /**
   * RF-12, RF-13 — inserts a variable at the caret. Numbered markers restart at `{{1}}` per
   * component; named markers go in empty so the operator types the name. Returns where the caret
   * should end up: inside `{{}}` for a named marker, after the marker for a numbered one.
   */
  insertParameter(control: AbstractControl, caret: number, existing: Array<string>): number {
    const text: string = control.value ?? '';
    const position = Math.min(Math.max(caret ?? text.length, 0), text.length);

    const marker = this.parameterFormat === 'NAMED' ? '{{}}' : `{{${this.nextPosition(existing)}}}`;
    control.setValue(`${text.slice(0, position)}${marker}${text.slice(position)}`);
    control.markAsDirty();

    return this.parameterFormat === 'NAMED' ? position + 2 : position + marker.length;
  }

  /**
   * The next numbered marker for a component. Uses the highest number already present rather than
   * the count, so deleting `{{2}}` of three does not produce a second `{{3}}`.
   */
  private nextPosition(existing: Array<string>): number {
    const numbers = existing.map((parameter) => Number(parameter)).filter((value) => Number.isInteger(value) && value > 0);
    return numbers.length > 0 ? Math.max(...numbers) + 1 : 1;
  }

  /** RF-18 — the header takes at most one variable. */
  get canInsertHeaderParameter(): boolean {
    return this.headerTextEnabled && this.headerParameters.length < this.limits.HEADER_PARAMETERS_MAX;
  }

  /** True when changing the variable type would rewrite markers already in the text (RF-14). */
  get hasParameters(): boolean {
    return this.headerParameters.length > 0 || this.bodyParameters.length > 0;
  }

  /**
   * RF-14 — rewrites every marker into the new form. Going to Number renumbers per component;
   * going to Name empties the markers so they can be typed. Samples that can no longer be matched
   * are discarded, which is why the cache is cleared.
   */
  rewriteParameters(target: WaTemplateParameterFormat): void {
    const rewrite = (value: string): string => {
      if (!value) {
        return value;
      }
      let position = 0;
      return value.replace(/\{\{\s*[a-zA-Z0-9_]*\s*\}\}/g, () => {
        position = position + 1;
        return target === 'NAMED' ? '{{}}' : `{{${position}}}`;
      });
    };

    this.exampleValues.clear();
    this.formGroup.get('parameterFormat').setValue(target, { emitEvent: false });
    this.formGroup.get('headerText').setValue(rewrite(this.formGroup.get('headerText').value), { emitEvent: false });
    this.formGroup.get('body').setValue(rewrite(this.formGroup.get('body').value), { emitEvent: false });
    this.syncParameters();
  }

  // ------------------------------------------------------------------ buttons

  /** Indexes of the buttons of one family, in submission order. */
  familyIndexes(family: 'CALL_TO_ACTION' | 'QUICK_REPLY'): Array<number> {
    return this.buttons.controls
      .map((control, index) => ({ control, index }))
      .filter((item) => BUTTON_FAMILY[item.control.get('type').value] === family)
      .map((item) => item.index);
  }

  countButtonsByType(type: WaTemplateButtonType): number {
    return this.buttons.controls.filter((control) => control.get('type').value === type).length;
  }

  private get maxByType(): { [key: string]: number } {
    return {
      QUICK_REPLY: this.limits.QUICK_REPLY_MAX,
      URL: this.limits.URL_BUTTON_MAX,
      PHONE_NUMBER: this.limits.PHONE_NUMBER_BUTTON_MAX,
      COPY_CODE: this.limits.COPY_CODE_BUTTON_MAX,
      VOICE_CALL: this.limits.VOICE_CALL_BUTTON_MAX,
      ORDER_DETAILS: this.limits.ORDER_DETAILS_BUTTON_MAX,
    };
  }

  maxOfType(type: WaTemplateButtonType): number {
    return this.maxByType[type];
  }

  /**
   * A template offers ONE way to call: either *Call* (PHONE_NUMBER) or *Call on WhatsApp*
   * (VOICE_CALL), never both.
   *
   * Established on 2026-08-08 from two pieces of evidence that only agree on this narrow rule:
   * Meta refused a real create of PHONE_NUMBER + VOICE_CALL with "Button Count Exceeded: You can't
   * have more than 1 button(s) in a message template", while Meta's own documentation publishes a
   * VOICE_CALL + URL template as a valid example. So the conflict is between the two call buttons,
   * not between VOICE_CALL and everything else — an earlier, broader reading of the same refusal
   * would have blocked the combination Meta documents as supported.
   */
  private static readonly CALL_BUTTONS: Array<WaTemplateButtonType> = ['PHONE_NUMBER', 'VOICE_CALL'];

  private get callButtonCount(): number {
    return TemplateFormEngine.CALL_BUTTONS.reduce((total, type) => total + this.countButtonsByType(type), 0);
  }

  /** True when adding `type` would give the template a second way to call. */
  conflictsWithCallButton(type: WaTemplateButtonType): boolean {
    return TemplateFormEngine.CALL_BUTTONS.indexOf(type) !== -1 && this.callButtonCount > 0;
  }

  /** RF-36, the per-type maxima of RF-38 … RF-44, and the single-call-button rule. */
  canAddButton(type: WaTemplateButtonType): boolean {
    // The order details format accepts its one button and nothing beside it, so a Pix template has
    // no button to add and no type to change into.
    if (this.pixMode) {
      return false;
    }
    return this.buttons.length < this.limits.BUTTONS_MAX
      && this.countButtonsByType(type) < this.maxByType[type]
      && !this.conflictsWithCallButton(type);
  }

  /**
   * Adds a button keeping each family contiguous: quick replies always sit after the call-to-action
   * buttons. Meta refuses a template whose quick replies are interleaved, so making the array order
   * structurally correct is cheaper than validating it after the fact.
   */
  addButton(type: WaTemplateButtonType): void {
    if (!this.canAddButton(type)) {
      return;
    }

    const group = this.buildButtonGroup(type);
    // A button the operator just added is not "untouched" — they touched it by adding it. Marking it
    // makes its empty required label report itself immediately, instead of silently disabling save.
    group.markAllAsTouched();

    if (BUTTON_FAMILY[type] === 'QUICK_REPLY') {
      this.buttons.push(group);
      return;
    }

    const firstQuickReply = this.familyIndexes('QUICK_REPLY')[0];
    if (firstQuickReply === undefined) {
      this.buttons.push(group);
    } else {
      this.buttons.insert(firstQuickReply, group);
    }
  }

  removeButton(index: number): void {
    if (this.pixMode) {
      return;
    }
    this.buttons.removeAt(index);
    this.buttons.markAsDirty();
  }

  /** RF-46 — moves a button between two positions of the same family. */
  moveButton(from: number, to: number): void {
    if (from === to) {
      return;
    }
    const control = this.buttons.at(from);
    this.buttons.removeAt(from);
    this.buttons.insert(to, control);
    this.buttons.markAsDirty();
  }

  /**
   * RF-50 — changes a button's type in place, keeping the label and dropping the fields that do not
   * belong to the new type. Refused when the target type is already at its maximum.
   */
  changeButtonType(index: number, type: WaTemplateButtonType): boolean {
    const current = this.buttons.at(index) as FormGroup;
    if (current.get('type').value === type) {
      return true;
    }
    if (this.pixMode) {
      return false;
    }
    if (this.countButtonsByType(type) >= this.maxByType[type]) {
      return false;
    }
    // Changing INTO a call button is refused when the template already has the other one.
    if (this.conflictsWithCallButton(type)) {
      return false;
    }

    const label = current.get('text').value;
    const replacement = this.buildButtonGroup(type, { text: type === 'COPY_CODE' ? null : label });
    // Same reasoning as addButton: the new type's own required fields must announce themselves.
    replacement.markAllAsTouched();

    // The family may have changed, so the button is re-placed rather than swapped in position.
    this.buttons.removeAt(index);
    if (BUTTON_FAMILY[type] === 'QUICK_REPLY') {
      this.buttons.push(replacement);
    } else {
      const firstQuickReply = this.familyIndexes('QUICK_REPLY')[0];
      this.buttons.insert(firstQuickReply === undefined ? this.buttons.length : firstQuickReply, replacement);
    }
    this.buttons.markAsDirty();
    return true;
  }

  private buildButtonGroup(type: WaTemplateButtonType, button?: { [key: string]: unknown }): FormGroup {
    const isUrl = type === 'URL';
    const isPhone = type === 'PHONE_NUMBER';
    const isCopyCode = type === 'COPY_CODE';

    // Two button types have a label the provider fixes and refuses to see changed. Resolved here,
    // from the type alone, so no caller can seed one with a different text.
    const fixedLabel = isCopyCode ? COPY_CODE_FIXED_LABEL : (type === 'ORDER_DETAILS' ? ORDER_DETAILS_FIXED_LABEL : null);

    return this.formBuilder.group({
      type: [type],
      // RF-44 fixes the Copy offer code label, so it is not a required free-text field. Same for the
      // Pix button (DEV-1992).
      text: [
        fixedLabel ?? (button?.['text'] ?? null),
        !!fixedLabel ? [] : [Validators.required, Validators.maxLength(this.limits.BUTTON_TEXT_MAX_LENGTH)],
      ],
      // RF-38 — Custom sub-type. Both values currently produce a free label; see the open question.
      subType: [button?.['subType'] ?? 'CUSTOM'],
      // RF-39, RF-40
      urlType: [button?.['urlType'] ?? 'STATIC'],
      url: [button?.['url'] ?? null, isUrl ? [Validators.required, Validators.maxLength(this.limits.BUTTON_URL_MAX_LENGTH)] : []],
      urlExample: [button?.['urlExample'] ?? null],
      // RF-43
      countryCode: [button?.['countryCode'] ?? '+55'],
      phoneNumber: [button?.['phoneNumber'] ?? null, isPhone ? [Validators.maxLength(this.limits.BUTTON_PHONE_NUMBER_MAX_LENGTH)] : []],
      // RF-44
      offerCode: [button?.['offerCode'] ?? null, isCopyCode ? [Validators.required, Validators.maxLength(this.limits.BUTTON_COPY_CODE_MAX_LENGTH)] : []],
    });
  }

  /** RF-48 — a Dynamic URL button carries one variable, so it needs one sample. */
  get dynamicUrlButtonIndexes(): Array<number> {
    return this.buttons.controls
      .map((control, index) => ({ control, index }))
      .filter((item) => item.control.get('type').value === 'URL' && item.control.get('urlType').value === 'DYNAMIC')
      .map((item) => item.index);
  }

  // ----------------------------------------------------------------- preview

  /** Replaces each variable by its sample, keeping the raw marker while the sample is empty (RF-27). */
  applyExamples(text: string, parameters: Array<string>, examples: Array<string>): string {
    let result = text ?? '';
    parameters.forEach((parameter, index) => {
      const example = examples[index];
      if (!!example) {
        result = result.replace(new RegExp(`\\{\\{\\s*${parameter}\\s*\\}\\}`, 'g'), example);
      }
    });
    return result;
  }

  // -------------------------------------------------------------- definition

  private buildNamedExamples(parameters: Array<string>, examples: Array<string>): Array<WaTemplateNamedExample> {
    return parameters.map((parameter, index) => ({ param_name: parameter, example: examples[index] }));
  }

  /** RF-40 — the `{{1}}` of a Dynamic URL is shown outside the field, so it is appended here. */
  resolveButtonUrl(button: { [key: string]: unknown }): string {
    const url = (button['url'] as string) ?? '';
    return button['urlType'] === 'DYNAMIC' ? `${url}{{1}}` : url;
  }

  buildDefinition(): WaTemplateDefinition {
    const value = this.formGroup.getRawValue();
    const parameterFormat: WaTemplateParameterFormat = value.parameterFormat;

    const definition: WaTemplateDefinition = {
      name: value.name,
      language: value.language,
      category: value.category,
      parameterFormat,
      // Trimmed: trailing whitespace is invisible in the field, carries no meaning, and is exactly
      // the kind of thing that sits between a variable and the end of the body.
      body: { text: (value.body ?? '').trim() },
    };

    // What unlocks the ORDER_DETAILS button. Without it Meta rejects the button type outright.
    if (this.pixMode) {
      definition.displayFormat = 'ORDER_DETAILS';
    }

    if (this.bodyParameters.length > 0) {
      if (parameterFormat === 'NAMED') {
        definition.body.namedExamples = this.buildNamedExamples(this.bodyParameters, value.bodyExamples);
      } else {
        definition.body.example = value.bodyExamples;
      }
    }

    // A header with text but no media is a TEXT header; NONE means no header component at all.
    const headerFormat: TemplateHeaderFormatOption = value.headerFormat === 'NONE' && !!value.headerText
      ? 'TEXT'
      : value.headerFormat;

    if (headerFormat !== 'NONE') {
      definition.header = { format: headerFormat === 'TEXT' ? 'TEXT' : headerFormat };
      if (headerFormat === 'TEXT') {
        definition.header.text = (value.headerText ?? '').trim();
        if (this.headerParameters.length > 0) {
          if (parameterFormat === 'NAMED') {
            definition.header.namedExamples = this.buildNamedExamples(this.headerParameters, value.headerExamples);
          } else {
            definition.header.example = value.headerExamples;
          }
        }
      }
      if (this.isMediaHeader(headerFormat)) {
        definition.header.handle = value.headerHandle;
      }
    }

    if (!!value.footer) {
      definition.footer = { text: (value.footer ?? '').trim() };
    }

    if (this.buttons.length > 0) {
      definition.buttons = (this.buttons.getRawValue() as Array<{ [key: string]: unknown }>)
        .map((button) => this.buildButtonDefinition(button));
    }

    return definition;
  }

  private buildButtonDefinition(button: { [key: string]: unknown }): WaTemplateButtonDefinition {
    const type = button['type'] as WaTemplateButtonType;
    const item: WaTemplateButtonDefinition = { type };

    switch (type) {
      case 'URL':
        item.text = button['text'] as string;
        item.url = this.resolveButtonUrl(button);
        if (button['urlType'] === 'DYNAMIC' && !!button['urlExample']) {
          item.example = button['urlExample'] as string;
        }
        break;
      case 'PHONE_NUMBER':
        item.text = button['text'] as string;
        item.phoneNumber = `${button['countryCode'] ?? ''}${(button['phoneNumber'] as string) ?? ''}`;
        break;
      case 'COPY_CODE':
        // The provider fixes the label of this button; only the code travels.
        item.example = button['offerCode'] as string;
        break;
      default:
        item.text = button['text'] as string;
        break;
    }

    return item;
  }

  /** Name and language are immutable; the category can only change while the template is not approved. */
  buildEditDefinition(definition: WaTemplateDefinition, template: WaTemplateRead): WaTemplateEditDefinition {
    const edit: WaTemplateEditDefinition = {
      header: definition.header,
      body: definition.body,
      footer: definition.footer,
      buttons: definition.buttons,
    };
    if (definition.parameterFormat !== this.initialParameterFormat) {
      edit.parameterFormat = definition.parameterFormat;
    }
    // The validity period is out of scope for this builder, but a template created elsewhere may
    // carry one — echo it back so saving content does not silently drop it.
    if (!!template?.message_send_ttl_seconds) {
      edit.messageSendTtlSeconds = template.message_send_ttl_seconds;
    }
    if (template?.status !== 'APPROVED' && definition.category !== template?.category) {
      edit.category = definition.category;
    }
    return edit;
  }

  // -------------------------------------------------------------- validation

  /**
   * Rules Meta enforces that a field validator cannot express, so the operator sees the message
   * before the request leaves the browser (RF-28).
   */
  validateRules(): Array<TemplateRuleProblem> {
    const problems: Array<TemplateRuleProblem> = [];
    const value = this.formGroup.getRawValue();
    const body: string = (value.body ?? '').trim();

    // Punctuation does not count as content for this rule. Meta refused
    // `Oiee {{1}}, pague o PIX de {{2}}! ` with "Leading or Trailing Params Not Allowed", so an
    // exclamation mark is not enough to keep a variable away from the edge — only real text is.
    // Checking the trimmed text alone let that body through, and the operator only found out from
    // Meta, after the round trip.
    //
    // The allowance is written INTO the anchors rather than stripped off the ends first: `{` and `}`
    // are themselves Unicode punctuation, so stripping would eat the variable's own braces and the
    // check would never fire.
    const startsWithParameter = /^[\s\p{P}\p{S}]*\{\{\s*[a-zA-Z0-9_]*\s*\}\}/u.test(body);
    const endsWithParameter = /\{\{\s*[a-zA-Z0-9_]*\s*\}\}[\s\p{P}\p{S}]*$/u.test(body);

    if (startsWithParameter || endsWithParameter) {
      problems.push({ key: 'channel.templateBuilder.errorDanglingParameter', field: 'body' });
    }

    if (this.parameterFormat === 'NAMED') {
      const namedParameters = [...this.headerParameters, ...this.bodyParameters];
      if (namedParameters.length !== this.namedParameterCount(value)) {
        problems.push({ key: 'channel.templateBuilder.errorEmptyNamedParameter', field: 'body' });
      }
      // Meta only accepts lowercase letters, digits and underscores in a parameter name. A template
      // saved with `{{Nome}}` is refused on create — or, worse, approved under a name the send side
      // can never match — so the case rule is enforced here, before the round trip.
      if (namedParameters.some((parameter) => !WA_TEMPLATE_NAMED_PARAMETER_PATTERN.test(parameter))) {
        problems.push({ key: 'channel.templateBuilder.errorNamedParameterCase', field: 'body' });
      }
    } else {
      const sorted = this.bodyParameters.map((parameter) => Number(parameter)).sort((first, second) => first - second);
      if (sorted.some((position, index) => position !== index + 1)) {
        problems.push({ key: 'channel.templateBuilder.errorSequentialParameters', field: 'body' });
      }
    }

    if (this.headerParameters.length > this.limits.HEADER_PARAMETERS_MAX) {
      problems.push({ key: 'channel.templateBuilder.errorHeaderParameters', field: 'headerText' });
    }

    if (extractWaTemplateParameters(value.footer).length > 0) {
      problems.push({ key: 'channel.templateBuilder.errorFooterParameters', field: 'footer' });
    }

    if (this.isMediaHeader(value.headerFormat) && !value.headerHandle) {
      problems.push({ key: 'channel.templateBuilder.errorMediaRequired', field: 'headerHandle' });
    }

    // Required button fields are reported here as well as inline: the buttons section can be far
    // below the save button, and a disabled save with no visible reason reads as broken.
    if (this.buttons.invalid) {
      problems.push({ key: 'channel.templateBuilder.errorButtonIncomplete' });
    }

    if (this.callButtonCount > 1) {
      problems.push({ key: 'channel.templateBuilder.errorSingleCallButton' });
    }

    (this.buttons.getRawValue() as Array<{ [key: string]: unknown }>).forEach((button, index) => {
      if (button['type'] === 'PHONE_NUMBER') {
        const digits = ((button['phoneNumber'] as string) ?? '').replace(/\D/g, '');
        if (digits.length === 0) {
          // RF-43 asks for two distinct messages: nothing typed, versus typed but not a number.
          problems.push({ key: 'channel.templateBuilder.errorPhoneRequired', field: `buttons.${index}.phoneNumber` });
        } else if (!/^\d{6,15}$/.test(digits)) {
          problems.push({ key: 'channel.templateBuilder.errorPhoneInvalid', field: `buttons.${index}.phoneNumber` });
        }
      }
      if (button['type'] === 'URL' && button['urlType'] === 'DYNAMIC' && !button['urlExample']) {
        problems.push({ key: 'channel.templateBuilder.errorUrlExample', field: `buttons.${index}.urlExample` });
      }
    });

    this.validateOrderDetailsRules(value, problems);

    return problems;
  }

  /**
   * The order details format's own rules, mirrored from the provider client's validator so the
   * operator reads them here instead of getting them back from Meta after a round-trip: exactly one
   * ORDER_DETAILS button, nothing beside it, a UTILITY or MARKETING category, and a header limited
   * to TEXT / IMAGE / DOCUMENT.
   */
  private validateOrderDetailsRules(value: { [key: string]: unknown }, problems: Array<TemplateRuleProblem>): void {
    if (!this.pixMode) {
      return;
    }

    const orderDetailsButtons = this.countButtonsByType('ORDER_DETAILS');
    if (orderDetailsButtons !== 1 || this.buttons.length !== orderDetailsButtons) {
      problems.push({ key: 'channel.templateBuilder.errorOrderDetailsSingleButton' });
    }

    if (WA_ORDER_DETAILS_CATEGORIES.indexOf(value['category'] as never) === -1) {
      problems.push({ key: 'channel.templateBuilder.errorOrderDetailsCategory' });
    }

    // 'NONE' is not a provider format — it means there is no header component at all, which is fine.
    const headerFormat = value['headerFormat'] as TemplateHeaderFormatOption;
    if (headerFormat !== 'NONE' && WA_ORDER_DETAILS_HEADER_FORMATS.indexOf(headerFormat as never) === -1) {
      problems.push({ key: 'channel.templateBuilder.errorOrderDetailsHeader', field: 'headerFormat' });
    }
  }

  /** Named markers left empty (`{{}}`) are not parameters yet — they are an unfinished edit. */
  private namedParameterCount(value: { [key: string]: unknown }): number {
    const count = (text: string): number => (text ?? '').match(/\{\{\s*[a-zA-Z0-9_]*\s*\}\}/g)?.length ?? 0;
    return count(value['headerText'] as string) + count(value['body'] as string);
  }

  // ---------------------------------------------------------------- populate

  /** Reads a provider template back into the form (RF-32, RF-33). */
  populate(template: WaTemplateRead): void {
    const components = (template.components ?? []) as Array<WaTemplateComponentRead>;
    const header = components.find((component) => component.type === 'HEADER');
    const body = components.find((component) => component.type === 'BODY');
    const footer = components.find((component) => component.type === 'FOOTER');
    const buttons = components.find((component) => component.type === 'BUTTONS');

    const parameterFormat: WaTemplateParameterFormat = resolveWaTemplateParameterFormat(template);
    this.initialParameterFormat = parameterFormat;

    this.formGroup.patchValue({
      name: template.name,
      language: template.language,
      category: template.category,
      parameterFormat,
      headerFormat: (header?.format as TemplateHeaderFormatOption) ?? 'NONE',
      headerText: header?.format === 'TEXT' ? header?.text : null,
      body: body?.text,
      footer: footer?.text,
    }, { emitEvent: false });

    this.cacheExamplesFromComponent(header, 'header');
    this.cacheExamplesFromComponent(body, 'body');

    this.buttons.clear();
    (buttons?.buttons ?? []).forEach((button) => {
      // `ButtonItemComponentObject` is a union whose members differ per type; the flat read below
      // touches fields that only exist on some of them.
      const raw = button as unknown as { [key: string]: unknown };
      this.buttons.push(this.buildButtonGroup(button.type as WaTemplateButtonType, this.readButton(raw)));
    });

    // The Graph API returns a media URL, not a reusable handle — saving again requires a new upload.
    if (this.isMediaHeader(header?.format as TemplateHeaderFormatOption)) {
      this.currentMediaUrl = header?.example?.header_handle?.[0] ?? null;
    }

    this.syncParameters();
  }

  /** Splits the provider's flat button back into the fields the form edits. */
  private readButton(button: { [key: string]: unknown }): { [key: string]: unknown } {
    const url = (button['url'] as string) ?? null;
    const dynamic = !!url && /\{\{\s*[a-zA-Z0-9_]*\s*\}\}\s*$/.test(url);
    const example = Array.isArray(button['example']) ? button['example'][0] : button['example'];
    const phone = (button['phone_number'] as string) ?? null;
    const country = this.splitPhone(phone);

    return {
      text: button['text'] ?? null,
      url: dynamic ? url.replace(/\{\{\s*[a-zA-Z0-9_]*\s*\}\}\s*$/, '') : url,
      urlType: dynamic ? 'DYNAMIC' : 'STATIC',
      urlExample: button['type'] === 'URL' ? example : null,
      offerCode: button['type'] === 'COPY_CODE' ? example : null,
      countryCode: country.code,
      phoneNumber: country.number,
    };
  }

  /**
   * Best-effort split of a stored `+55119...` into calling code and number. The provider stores one
   * string, so the boundary is recovered by matching the longest known calling code.
   */
  private splitPhone(phone: string): { code: string, number: string } {
    if (!phone) {
      return { code: '+55', number: null };
    }
    // The calling codes carry a leading '+'; a stored number without one would otherwise match
    // nothing and keep its country digits in the number field.
    const normalized = phone.startsWith('+') ? phone : `+${phone}`;
    const codes = TemplateFormEngine.callingCodes
      .filter((code) => normalized.startsWith(code))
      .sort((first, second) => second.length - first.length);
    return codes.length > 0
      ? { code: codes[0], number: normalized.slice(codes[0].length) }
      : { code: '+55', number: normalized.replace(/^\+/, '') };
  }

  /** Filled by the component from the shared country list, so the engine holds no country data. */
  static callingCodes: Array<string> = [];

  private cacheExamplesFromComponent(component: WaTemplateComponentRead, scope: string): void {
    if (!component) {
      return;
    }

    const namedExamples = scope === 'header'
      ? component.example?.header_text_named_params
      : component.example?.body_text_named_params;

    if (namedExamples?.length) {
      namedExamples.forEach((named) => this.exampleValues.set(`${scope}:${named.param_name}`, named.example));
      return;
    }

    const positionalExamples = scope === 'header'
      ? component.example?.header_text
      : component.example?.body_text?.[0];

    const parameters = extractWaTemplateParameters(component.text);
    (positionalExamples ?? []).forEach((example, index) => {
      const parameter = parameters[index];
      if (!!parameter) {
        this.exampleValues.set(`${scope}:${parameter}`, example);
      }
    });
  }
}
